// Filename: src/screens/books/Pagination.jsx
// Current Date and Time (UTC): 2025-02-04 18:17:22
// Current User's Login: raghavkunkee

/**
 * Pagination Component
 * Compact pagination controls with consistent styling.
 * Features:
 * - Minimal height design (40px)
 * - Direct page navigation
 * - Page size selection
 * - Responsive layout
 * 
 * @author raghavkunkee
 * @version 1.1.0
 */

import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import styled from 'styled-components';

const PaginationContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 1rem;
    background: #d14141;
    border-radius: 0 0 4px 4px;
    margin: 0 1rem;
    gap: 1rem;
    height: 40px;
    color: ${props => props.theme.colors.white};

    @media (max-width: 768px) {
        flex-direction: column;
        height: auto;
        padding: 0.5rem 1rem;
        gap: 0.5rem;
    }
`;

const NavigationGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;

    @media (max-width: 768px) {
        width: 100%;
        justify-content: center;
    }
`;

const PageButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: ${props => props.circle ? '50%' : '4px'};
    background: ${props => props.active ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
    color: ${props => props.theme.colors.white};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    opacity: ${props => props.disabled ? 0.5 : 1};
    transition: all 0.2s;
    font-size: 0.75rem;

    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.8);
    }

    &:active:not(:disabled) {
        background: rgba(255, 255, 255, 0.2);
    }

    .pi {
        font-size: 0.75rem;
        margin: 0;
    }
`;

const PageInput = styled(InputNumber)`
    .p-inputnumber-input {
        width: 3rem;
        height: 28px;
        text-align: center;
        border: 1px solid rgba(255, 255, 255, 0.5);
        border-radius: 4px;
        background: transparent;
        color: ${props => props.theme.colors.white};
        font-size: 0.875rem;
        padding: 0.25rem;
        
        &:focus {
            box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
            border-color: rgba(255, 255, 255, 0.8);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
`;

const StyledDropdown = styled(Dropdown)`
    .p-dropdown {
        height: 28px;
        background: transparent;
        border: 1px solid rgba(255, 255, 255, 0.5);
        min-width: 5rem;
        
        .p-dropdown-label {
            color: ${props => props.theme.colors.white};
            padding: 0.25rem 0.5rem;
            font-size: 0.875rem;
            line-height: 1.5;
        }
        
        .p-dropdown-trigger {
            width: 2rem;
            color: ${props => props.theme.colors.white};

            .pi {
                font-size: 0.75rem;
            }
        }

        &:not(.p-disabled):hover {
            border-color: rgba(255, 255, 255, 0.8);
        }

        &:not(.p-disabled).p-focus {
            box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
            border-color: rgba(255, 255, 255, 0.8);
        }
    }

    .p-dropdown-panel {
        .p-dropdown-items {
            padding: 0.25rem 0;
            
            .p-dropdown-item {
                padding: 0.25rem 0.5rem;
                font-size: 0.875rem;
                color: ${props => props.theme.colors.font_dark};
                min-height: 28px;
                
                &:hover {
                    background: rgba(188, 50, 50, 0.1);
                }
                
                &.p-highlight {
                    background: #bc3232;
                    color: ${props => props.theme.colors.white};
                }
            }
        }
    }
`;

const InputGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    label {
        font-size: 0.75rem;
        color: ${props => props.theme.colors.white};
        font-weight: 500;
        white-space: nowrap;
    }

    @media (max-width: 768px) {
        width: 100%;
        justify-content: center;
    }
`;

const PageInfo = styled.div`
    font-size: 0.75rem;
    color: ${props => props.theme.colors.white};
    font-weight: 500;
    white-space: nowrap;
`;

const Divider = styled.div`
    width: 1px;
    height: 24px;
    background: rgba(255, 255, 255, 0.2);
    margin: 0 0.5rem;

    @media (max-width: 768px) {
        display: none;
    }
`;

const Pagination = ({ 
    page, 
    setPage, 
    size, 
    setSize, 
    lastPage, 
    sizeList,
    disabled 
}) => {
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage && !disabled) {
            setPage(newPage);
        }
    };

    const handlePageInput = (value) => {
        if (value >= 1 && value <= lastPage) {
            setPage(value);
        }
    };

    const handleSizeChange = (e) => {
        setSize(e.value);
        setPage(1);
    };

    return (
        <PaginationContainer>
            <NavigationGroup>
                <PageButton
                    onClick={() => handlePageChange(1)}
                    disabled={page === 1 || disabled}
                    title="First Page"
                >
                    <i className="pi pi-angle-double-left" />
                </PageButton>

                <PageButton
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1 || disabled}
                    title="Previous Page"
                >
                    <i className="pi pi-angle-left" />
                </PageButton>

                <PageButton
                    active
                    circle
                    disabled={disabled}
                >
                    {page}
                </PageButton>

                <PageButton
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === lastPage || disabled}
                    title="Next Page"
                >
                    <i className="pi pi-angle-right" />
                </PageButton>

                <PageButton
                    onClick={() => handlePageChange(lastPage)}
                    disabled={page === lastPage || disabled}
                    title="Last Page"
                >
                    <i className="pi pi-angle-double-right" />
                </PageButton>
            </NavigationGroup>

            <Divider />

            <InputGroup>
                <label>Page:</label>
                <PageInput
                    value={page}
                    onValueChange={(e) => handlePageInput(e.value)}
                    min={1}
                    max={lastPage}
                    disabled={disabled}
                    useGrouping={false}
                    showButtons={false}
                />
                <PageInfo>of {lastPage}</PageInfo>
            </InputGroup>

            <Divider />

            <InputGroup>
                <label>Items per page:</label>
                <StyledDropdown
                    value={size}
                    options={sizeList}
                    onChange={handleSizeChange}
                    disabled={disabled}
                    optionLabel="name"
                />
            </InputGroup>
        </PaginationContainer>
    );
};

export default Pagination;